import { useState, useEffect, useRef } from "react";
import { Phone, Menu, X, ChevronDown, Check, Star, BadgeCheck } from "lucide-react";
import logo from "@/assets/oz-move-logo.webp";
import heroTruck from "@/assets/hero-truck.webp";
import { googleReviewSummary, googleBusinessUrl } from "@/data/reviews";

// Official-style multicolor Google "G" logo (matches SocialProofStats usage).
const GoogleGIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

const trustIndicators = [
  "From $130/hr",
  "2 Professional Movers",
  "4.5 Tonne Moving Truck",
];
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

// Priority order: items hide first as screen shrinks (Reviews → Services → How It Works)
const navLinks = [
  { label: "How It Works", href: "/#how-it-works", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
  { label: "Services", href: "/#services", showClass: "xl:inline-flex", hideClass: "xl:hidden" },
  { label: "Reviews", href: "/#reviews", showClass: "2xl:inline-flex", hideClass: "2xl:hidden" },
  { label: "Get a Quote", href: "/#quote", showClass: "lg:inline-flex", hideClass: "lg:hidden" },
];

const HeroSection = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dividerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const divider = dividerRef.current;
      const header = headerRef.current;
      if (!divider || !header) return;
      // Trigger when the black feature bar (TrustStrip, which begins at the
      // bottom edge of the diagonal divider) reaches the bottom of the
      // sticky header. Keeps the header yellow throughout the hero.
      const trigger = divider.getBoundingClientRect().bottom <= header.offsetHeight;
      setScrolled((prev) => (prev !== trigger ? trigger : prev));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const headerBg = scrolled ? "bg-white shadow-md" : "bg-[#FFC300]";
  const drawerBg = scrolled ? "bg-white" : "bg-[#FFC300]";

  return (
    <>
      {/* Sticky Header — lifted out of the hero section so it stays sticky
          across the whole page. Blends into the hero while at the top,
          transitions to white once the black feature bar reaches it. */}
      <header
        ref={headerRef}
        className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-300 ease-out ${headerBg}`}
      >
        <div className="container flex flex-nowrap items-center justify-between gap-4 py-3 whitespace-nowrap">
          {/* LEFT: Hamburger + Logo */}
          <div className="flex flex-nowrap items-center gap-3 min-w-0">
            <button
              className="text-[#1a1a1a] p-1 md:hidden"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <a href="/">
              <img src={logo} alt="OZ MOVE CO. Logo" className="h-16 md:h-24 w-auto shrink-0" width="288" height="192" />
            </a>
            {/* Desktop nav links */}
            <nav className="hidden md:flex flex-nowrap items-center gap-6 ml-6">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className={`hidden ${link.showClass} text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap`}
                >
                  {link.label}
                </a>
              ))}
              {/* "More" dropdown surfaces nav items hidden at the current breakpoint */}
              <DropdownMenu>
                <DropdownMenuTrigger
                  aria-label="More navigation"
                  className="hidden md:inline-flex 2xl:hidden items-center gap-1 text-[#1a1a1a]/80 font-semibold text-sm hover:text-[#1a1a1a] transition-colors whitespace-nowrap focus:outline-none"
                >
                  More <ChevronDown className="w-4 h-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className={`border-[#1a1a1a]/10 ${drawerBg}`}>
                  {navLinks
                    .filter((l) => l.label !== "Get a Quote")
                    .map((link) => (
                      <DropdownMenuItem
                        key={link.label}
                        asChild
                        className={`${link.hideClass} text-[#1a1a1a] font-semibold focus:bg-[#1a1a1a]/10 focus:text-[#1a1a1a]`}
                      >
                        <a href={link.href}>{link.label}</a>
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>

          {/* RIGHT: Phone → Get Quote → Book Now */}
          <div className="flex flex-nowrap items-center gap-2 md:gap-3 shrink-0">
            <a
              href="tel:1800556654"
              data-call-source="header_desktop"
              aria-label="Call 1800 556 654"
              className="inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-3 md:px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">1800 556 654</span>
            </a>
            <a
              href="/#quote"
              className="hidden md:inline-flex border-2 border-[#1a1a1a] text-[#1a1a1a] px-4 py-2 font-bold text-sm rounded-full hover:bg-[#1a1a1a]/5 transition-colors whitespace-nowrap shrink-0"
            >
              Get Quote
            </a>
            <a
              href="/#quote"
              className="inline-flex bg-[#1a1a1a] text-[#FFC300] px-4 md:px-5 py-2 font-bold text-sm rounded-full hover:opacity-90 transition-opacity whitespace-nowrap shrink-0"
            >
              Book Now
            </a>
          </div>
        </div>

        {/* Mobile nav drawer */}
        {menuOpen && (
          <nav className={`md:hidden border-t border-[#1a1a1a]/10 ${drawerBg}`}>
            <div className="container py-4 flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-[#1a1a1a] font-bold text-base py-2 border-b border-[#1a1a1a]/10 last:border-b-0"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="tel:1800556654"
                data-call-source="header_mobile_menu"
                className="flex items-center gap-2 text-[#1a1a1a] font-bold text-base py-2"
              >
                <Phone className="w-5 h-5" />
                1800 556 654
              </a>
            </div>
          </nav>
        )}
      </header>

      <section className="bg-[#FFC300] relative overflow-hidden">
        {/* Hero Content */}
        <div className="relative z-10 container">
          <div className="flex flex-col lg:flex-row lg:items-end min-h-[340px] md:min-h-[440px] lg:min-h-[460px] xl:min-h-[500px]">
            <div className="w-full lg:w-1/2 flex flex-col justify-center items-center text-center pt-4 md:pt-8 lg:pt-12 pb-4 md:pb-8 lg:pb-12 px-0 lg:px-8">
              <h1 className="text-5xl md:text-6xl xl:text-6xl font-black text-[#1a1a1a] leading-[1.08] tracking-tight">
                Move Without the Stress
              </h1>
              <p className="mt-4 md:mt-6 text-lg md:text-xl xl:text-lg text-[#1a1a1a]/75 max-w-[600px] font-medium leading-relaxed">
                No delays. No hidden costs. Just a reliable team that shows up and gets it done right.
              </p>
              <div className="mt-3 md:mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 w-full">
                {trustIndicators.map((label) => (
                  <div key={label} className="inline-flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#1a1a1a] shrink-0" strokeWidth={3.5} />
                    <span className="text-base font-medium text-[#1a1a1a]/75 whitespace-nowrap">{label}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 md:mt-7 flex flex-col sm:flex-row gap-4 justify-center w-full sm:w-auto">
                <a
                  href="/#how-it-works"
                  className="border-2 border-[#1a1a1a] text-[#1a1a1a] px-6 py-3 md:px-8 md:py-4 text-lg font-bold rounded-md hover:bg-[#1a1a1a]/5 transition-colors text-center"
                >
                  See How It Works
                </a>
                <a
                  href="/#quote"
                  className="bg-[#1a1a1a] text-[#FFC300] px-6 py-3 md:px-8 md:py-4 text-lg font-bold rounded-md hover:opacity-90 transition-opacity text-center"
                >
                  Get a Free Quote
                </a>
              </div>
              {/* Google Reviews trust badge — compact on-brand pill.
                  One line: [G] ★★★★★ 5.0 · 27 reviews. Clickable, opens
                  the Google Business Profile (@/data/reviews source). */}
              <div className="mt-4 md:mt-6 flex justify-center w-full">
                <a
                  href={googleBusinessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View our Google Reviews"
                  className="group inline-flex items-center gap-1.5 bg-[#1a1a1a] text-[#FFC300] rounded-full px-4 py-2 cursor-pointer whitespace-nowrap transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC300]/40"
                >
                  <GoogleGIcon className="w-4 h-4 shrink-0" />
                  <div className="flex items-center gap-0.5" aria-hidden="true">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 text-[#FFC300] fill-[#FFC300]" strokeWidth={0} />
                    ))}
                  </div>
                  <span className="text-sm font-bold tabular-nums leading-none">
                    {googleReviewSummary.rating.toFixed(1)}
                  </span>
                  <span className="text-sm font-medium text-[#FFC300]/60 leading-none">·</span>
                  <span className="text-sm font-medium text-[#FFC300]/80 leading-none">
                    {googleReviewSummary.reviewCount} reviews
                  </span>
                </a>
              </div>
            </div>
            <div className="w-full lg:w-1/2 relative flex items-end justify-center lg:justify-end mt-0 lg:mt-0 pb-4 md:pb-0">
              {/* Subtle motion accents behind the truck: three tapered streaks
                  running bottom-left to upper-right. Purely decorative. */}
              <div className="hero-streaks pointer-events-none absolute inset-0 overflow-hidden z-0" aria-hidden="true">
                <span className="hero-streak hero-streak--1" />
                <span className="hero-streak hero-streak--2" />
                <span className="hero-streak hero-streak--3" />
              </div>
              <img
                src={heroTruck}
                alt="OZ MOVE CO. moving truck with kangaroo mascot"
                width="1100"
                height="734"
                className="relative z-10 w-[72%] max-w-[400px] md:w-[72%] md:max-w-[600px] lg:w-[98%] lg:max-w-none lg:max-h-[360px] xl:max-h-[400px] 2xl:w-[112%] 2xl:max-h-[440px] h-auto object-contain object-bottom lg:object-right-bottom lg:-mr-4 xl:-mr-6 2xl:-mr-24"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Diagonal divider — black wedge overlapping up into the yellow hero
          so the truck wheels appear to sit right on the black feature strip. */}
      <div ref={dividerRef} className="relative -mt-8 md:-mt-10">
        <svg viewBox="0 0 1440 40" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" preserveAspectRatio="none">
          <polygon points="0,0 1440,32 1440,40 0,40" fill="#111111" />
        </svg>
      </div>
    </>
  );
};

export default HeroSection;
